/** Fury Score — single source of truth for site-wide firm ranking.
 *  Deterministic 0-10 composite score from DB fields only.
 *  Used by: FirmTopSummary, BestPropFirms2026Page, anywhere ranking matters.
 */

import { estimatePathDays } from "@/utils/pathEstimator";
import { computeTrueCost } from "@/utils/trueCostUtils";
import { isPublicEvalType } from "@/utils/constants";

/**
 * Benchmark tier selection for cost scoring.
 * Priority: 50K > nearest standard tier > first public tier.
 * Ensures deterministic, SSOT-driven tier selection.
 */
const BENCHMARK_PRIORITY = ["50K", "100K", "25K", "150K", "200K", "250K", "300K", "10K"];

export const getBenchmarkTier = (firm) => {
  const publicTiers = (firm.pricing || []).filter(p => isPublicEvalType(p.evaluation_type));
  if (!publicTiers.length) return null;
  for (const sizeKey of BENCHMARK_PRIORITY) {
    const match = publicTiers.find(p => (p.size || "").includes(sizeKey));
    if (match) return match;
  }
  return publicTiers[0];
};

/** Parse first dollar amount from a string like "$149" or "$98 (10K) / $143 (25K)" */
export const parseDollar = (str) => {
  if (!str) return null;
  const m = str.match(/\$[\d,]+/);
  return m ? parseInt(m[0].replace(/[$,]/g, ""), 10) : null;
};

/** Parse max profit split from string, matching only %-suffixed numbers.
 *  "100% / 90%" → 100, "80% (90% after $50K, 95% after $100K withdrawn)" → 95
 *  Ignores dollar thresholds and other non-% numeric text. */
export const parseSplit = (str) => {
  if (!str) return null;
  const pctMatches = str.match(/(\d+)%/g);
  if (!pctMatches?.length) return null;
  return Math.max(...pctMatches.map(m => parseInt(m)));
};

/** Compute Fury Score (ffScore) from firm data.
 *  Components: trust, split, payout, cost, rules → averaged to 0-10.
 */
export const computeFFScore = (firm) => {
  const components = {};
  let sum = 0;
  let count = 0;

  // 1. Verified Trader Rating (first-party, sentiment-derived 0-5 → 0-10).
  //    Sourced from FuturesFury's own Community Pulse. No third-party review data.
  const rating = firm.verified_trader_rating;
  if (rating && rating > 0) {
    components.trust = Math.min(10, Math.round(rating * 2 * 10) / 10);
    sum += components.trust;
    count++;
  }

  // 2. Profit split (max % → tiered bands)
  //    100→10, 95→9, 90→8, 85→7, 80→6, <80→5, missing→6.5
  const splitMax = parseSplit(firm.profit_split);
  if (splitMax) {
    let splitScore;
    if (splitMax >= 100) splitScore = 10;
    else if (splitMax >= 95) splitScore = 9;
    else if (splitMax >= 90) splitScore = 8;
    else if (splitMax >= 85) splitScore = 7;
    else if (splitMax >= 80) splitScore = 6;
    else splitScore = 5;
    components.split = splitScore;
    sum += splitScore;
    count++;
  } else {
    components.split = 6.5;
    sum += 6.5;
    count++;
  }

  // 3. Payout speed (data-driven: estimatePathDays → score)
  const ptp = estimatePathDays(firm);
  const payDays = ptp.total;
  if (payDays > 0) {
    let payScore;
    if (payDays <= 5) payScore = 10;
    else if (payDays <= 7) payScore = 9.5;
    else if (payDays <= 10) payScore = 9;
    else if (payDays <= 14) payScore = 8.5;
    else if (payDays <= 20) payScore = 7.5;
    else if (payDays <= 30) payScore = 6;
    else payScore = 5;
    components.payout = payScore;
    sum += payScore;
    count++;
  } else {
    // No usable data → neutral fallback
    components.payout = 6;
    sum += 6;
    count++;
  }

  // 4. True Cost (eval + activation + 1 reset on benchmark tier)
  //    Bands: ≤$150→10, ≤$225→9, ≤$300→8, ≤$400→7, ≤$500→6, >$500→5
  //    Confidence penalty: -0.5 for partial/estimated, floor at 5
  const benchmarkTier = getBenchmarkTier(firm);
  if (benchmarkTier) {
    const tc = computeTrueCost(firm, benchmarkTier);
    if (tc && tc.totalCost > 0) {
      let costScore;
      if (tc.totalCost <= 150) costScore = 10;
      else if (tc.totalCost <= 225) costScore = 9;
      else if (tc.totalCost <= 300) costScore = 8;
      else if (tc.totalCost <= 400) costScore = 7;
      else if (tc.totalCost <= 500) costScore = 6;
      else costScore = 5;
      if (tc.confidence === "partial" || tc.confidence === "estimated") {
        costScore = Math.max(5, costScore - 0.5);
      }
      components.cost = Math.round(costScore * 10) / 10;
      sum += components.cost;
      count++;
    }
  }
  if (components.cost === undefined) {
    components.cost = 6;
    sum += 6;
    count++;
  }

  // 5. Rules flexibility (no consistency = better, news trading = better)
  let rulesScore = 5;
  const consistency = (firm.consistency_rule || "").toLowerCase();
  if (consistency.includes("none") || !consistency) rulesScore += 2;
  else if (consistency.includes("50%")) rulesScore -= 1;
  if (firm.news_trading === true) rulesScore += 1.5;
  if (firm.algos_allowed === true) rulesScore += 1;
  components.rules = Math.min(10, Math.round(rulesScore * 10) / 10);
  sum += components.rules;
  count++;

  const total = count > 0 ? Math.round((sum / count) * 10) / 10 : 0;
  return { total: Math.min(10, total), components };
};

/** Ordered component metadata for rendering breakdown rows */
export const COMPONENT_META = [
  { key: "trust", label: "Trust" },
  { key: "payout", label: "Payout Speed" },
  { key: "split", label: "Profit Split" },
  { key: "cost", label: "True Cost" },
  { key: "rules", label: "Rule Flexibility" },
];

/** Generate reason text for each score component */
export const getComponentReason = (key, value, firm) => {
  const reasons = {
    trust: () => {
      if (value >= 8.5) return `Strong trader sentiment — well above average`;
      if (value >= 7) return `Solid trader sentiment`;
      return `Mixed trader sentiment — room for improvement`;
    },
    split: () => {
      const maxPct = parseSplit(firm.profit_split);
      if (!maxPct) return "Split data unavailable";
      if (maxPct >= 100) return `Up to ${maxPct}% — among the highest available`;
      if (maxPct >= 90) return `Up to ${maxPct}% — competitive split`;
      if (maxPct >= 85) return `${maxPct}% — solid, industry standard`;
      return `${maxPct}% — below average split`;
    },
    payout: () => {
      const ptp = estimatePathDays(firm);
      const d = ptp.total;
      if (d <= 5) return `~${d} days to payout — fastest tier`;
      if (d <= 10) return `~${d} days to payout — very fast`;
      if (d <= 14) return `~${d} days to payout — competitive speed`;
      if (d <= 20) return `~${d} days to payout — moderate wait`;
      if (d <= 30) return `~${d} days to payout — standard timeline`;
      if (d > 30) return `~${d} days to payout — longer path`;
      return `${firm.payout_frequency || "Unknown cadence"}`;
    },
    cost: () => {
      const tier = getBenchmarkTier(firm);
      if (!tier) return "No public pricing data available";
      const tc = computeTrueCost(firm, tier);
      if (!tc || tc.totalCost <= 0) return "Cost data unavailable";
      const prefix = tc.confidence === "exact" ? "$" : "~$";
      const total = tc.totalCost;
      if (total <= 150) return `${prefix}${total} total to funded — excellent value`;
      if (total <= 225) return `${prefix}${total} total to funded — very competitive`;
      if (total <= 300) return `${prefix}${total} total to funded — solid value`;
      if (total <= 400) return `${prefix}${total} total to funded — moderate investment`;
      if (total <= 500) return `${prefix}${total} total to funded — above average cost`;
      return `${prefix}${total} total to funded — premium pricing`;
    },
    rules: () => {
      const c = (firm.consistency_rule || "").toLowerCase();
      const parts = [];
      if (c.includes("none") || !c) parts.push("No consistency rule");
      else parts.push(`${firm.consistency_rule} consistency`);
      if (firm.news_trading === true) parts.push("news trading OK");
      else if (firm.news_trading === false) parts.push("news restricted");
      return parts.join(", ");
    },
  };
  return reasons[key] ? reasons[key]() : "";
};

/** Score bar color */
export const getBarColor = (score) => {
  if (score >= 8) return "bg-emerald-500";
  if (score >= 6.5) return "bg-amber-500";
  return "bg-rose-500";
};

/** Score ring color */
export const getScoreColor = (score) => {
  if (score >= 8) return { ring: "text-emerald-500", bg: "bg-emerald-500/10 border-emerald-500/30", text: "text-emerald-500" };
  if (score >= 6.5) return { ring: "text-amber-500", bg: "bg-amber-500/10 border-amber-500/30", text: "text-amber-500" };
  return { ring: "text-rose-500", bg: "bg-rose-500/10 border-rose-500/30", text: "text-rose-500" };
};
